import { useMemo, useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { styled } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Button from '@components/Button';
import FiltersModal from '@components/FiltersModal';
import { Chip } from '@components/Chip';
import { FiltersFormCreator } from '@components/FiltersFormCreator';
import { FiltersIcon } from 'assets/images/icons';
import { createUpdatedChips, getChipsLabel, isChipActive, hasActiveChips } from '@helpers/filters';
import { formatTimeframeChipValue } from '@helpers/dates';
import { AUTHOR, FORMAT } from '@constants/profiles-media';
import { useTranslation, useLocations } from '@hooks';
import { AccIntegrationName } from '@constants/integrations';

export const FiltersWrapper = styled('div')({
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    rowGap: '8px',
    '@media print': {
        '& button': {
            display: 'none',
        },
    },
});

const FiltersPanel = ({
    className,
    setFiltersOpened,
    filtersNumber,
    filterOpened,
    onApplyFilters,
    disabled = false,
    filter,
    setFilter,
    withChips = true,
    type,
    setFilterNumber,
}) => {
    const firstRender = useRef(true);
    const { t } = useTranslation();
    const language = useSelector(state => state.auth.user?.language);
    const [filterChips, setFilterChips] = useState({
        companies: [],
        formats: [],
        categories: [],
        authors: [],
        timeframe: {},
    });
    const [refetchFilters, setRefetchFilters] = useState(false);

    const isMobile = useMediaQuery('@media (max-width: 768px)');
    const { getLocationsOptions } = useLocations();

    useEffect(() => {
        if (refetchFilters) {
            setRefetchFilters(false);
            applyChips();
            onApplyFilters();
        }
    }, [refetchFilters]);

    useEffect(() => {
        if (firstRender.current && filter.companies.length > 0) {
            applyChips();
            firstRender.current = false;
        }
    }, [filter]);

    const options = useMemo(() => {
        //locationsOptions
        const filterCb = l => l.profiles.some(i => i.type === AccIntegrationName.Google);
        const locationsOptions = getLocationsOptions(filterCb);

        //formatsOptions
        const formatsOptions = [
            { value: FORMAT.PHOTO, label: t('common.photo') },
            { value: FORMAT.VIDEO, label: t('common.video') },
        ];

        //categoriesOptions
        const categoriesOptions = [
            { value: 0, label: t('common.none') },
            { value: 1, label: t('ProfilesMedia.exterior') },
            { value: 2, label: t('ProfilesMedia.interior') },
            { value: 3, label: t('ProfilesMedia.product') },
            { value: 4, label: t('ProfilesMedia.atWork') },
            { value: 5, label: t('ProfilesMedia.foodAndDrink') },
            { value: 6, label: t('ProfilesMedia.menu') },
            { value: 7, label: t('ProfilesMedia.commonArea') },
            { value: 8, label: t('ProfilesMedia.rooms') },
            { value: 9, label: t('ProfilesMedia.teams') },
        ];

        //authorsOptions
        const authorsOptions = [
            { value: AUTHOR.BUSINESS, label: t('common.business') },
            { value: AUTHOR.CUSTOMER, label: t('common.customer') },
        ];

        return {
            companies: locationsOptions,
            formats: formatsOptions,
            categories: categoriesOptions,
            authors: authorsOptions,
        };
    }, [filter]);

    useEffect(() => {
        const allFilters = { ...filter, ...filterChips };
        const filterNumber = Object.keys(allFilters).reduce((total, key) => {
            let increment = 0;

            switch (key) {
                case 'timeframe':
                    increment = filterChips[key]?.start ? 1 : 0;
                    break;
                default:
                    increment = filterChips[key]?.length > 0 ? 1 : 0;
                    break;
            }

            return total + increment;
        }, 0);

        setFilterNumber(filterNumber);
    }, [filterChips, filter]);

    const update = {
        companies: items => {
            const selectedValues = items.length ? items : [];
            setFilter(prev => ({ ...prev, companies: selectedValues }));
        },
        formats: value => setFilter(prev => ({ ...prev, formats: value })),
        categories: value => setFilter(prev => ({ ...prev, categories: value })),
        authors: value => setFilter(prev => ({ ...prev, authors: value })),
        timeframe: data => {
            setFilter(prev => ({ ...prev, timeframe: { start: data[0], end: data[1] } }));
        },
    };

    const CHIPS_ACTIONS = {
        companies: {
            reset: () => update.companies(options.companies),
            value: getChipsLabel(filterChips?.companies),
        },
        formats: {
            reset: () => setFilter(prev => ({ ...prev, formats: [FORMAT.PHOTO, FORMAT.VIDEO] })),
            value: getChipsLabel(filterChips?.formats),
        },
        categories: {
            reset: () => setFilter(prev => ({ ...prev, categories: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9] })),
            value: getChipsLabel(filterChips?.categories),
        },
        authors: {
            reset: () => setFilter(prev => ({ ...prev, authors: [AUTHOR.BUSINESS, AUTHOR.CUSTOMER] })),
            value: getChipsLabel(filterChips?.authors),
        },
        timeframe: {
            reset: () => setFilter(prev => ({ ...prev, timeframe: { start: null, end: null } })),
            value: filterChips?.timeframe?.start
                ? formatTimeframeChipValue(filterChips.timeframe.start, filterChips.timeframe.end, language)
                : '',
        },
    };

    const applyChips = () => {
        const updatedChips = createUpdatedChips(filterChips, filter, options);
        setFilterChips(updatedChips);
    };

    const resetFilters = () => Object.values(CHIPS_ACTIONS).forEach(({ reset }) => reset());

    const createFormOptions = (filter, update, options) => {
        const formOptions = [];

        for (let key in filter) {
            if (Array.isArray(filter[key])) {
                formOptions.push({
                    type: key === 'companies' ? 'autocomplete' : 'multiSelect',
                    label: t(`filters.${key}`),
                    value: filter[key],
                    onChange: update[key],
                    options: options[key],
                    countable: true,
                    error: !filter[key].length,
                });
            } else if (key === 'timeframe') {
                formOptions.push({
                    type: 'timeframe',
                    label: t(`filters.${key}`),
                    value: [filter[key].start, filter[key].end],
                    onChange: update[key],
                });
            }
        }

        return formOptions;
    };

    const isValid = Object.keys(filter).every(key => {
        if (Array.isArray(filter[key])) {
            return !!filter[key].length;
        } else {
            return true;
        }
    });

    const hasDraftFilters = useMemo(
        () => !isValid || hasActiveChips(createUpdatedChips(filterChips, filter, options)),
        [isValid, filterChips, filter, options],
    );

    return (
        <FiltersWrapper>
            <Button
                className={className}
                variant="outlined"
                onClick={() => setFiltersOpened(true)}
                startIcon={<FiltersIcon />}
                disabled={disabled}
                sx={{ marginRight: '8px' }}
            >
                {t('buttons.filters')}
                {filtersNumber > 0 ? ` (${filtersNumber})` : ''}
            </Button>
            {withChips &&
                !isMobile &&
                Object.keys(filterChips).map(
                    key =>
                        isChipActive(filterChips[key]) && (
                            <Chip
                                key={key}
                                label={CHIPS_ACTIONS[key].value}
                                onDelete={() => {
                                    CHIPS_ACTIONS[key].reset();
                                    setRefetchFilters(true);
                                }}
                            />
                        ),
                )}
            <FiltersModal
                isOpen={filterOpened}
                handleClose={() => setFiltersOpened(false)}
                onApply={() => {
                    setRefetchFilters(true);
                    setFiltersOpened(false);
                }}
                applyDisabled={!isValid}
                onClear={resetFilters}
                clearDisabled={!hasDraftFilters}
            >
                <FiltersFormCreator type={type} options={createFormOptions(filter, update, options)} />
            </FiltersModal>
        </FiltersWrapper>
    );
};

export default FiltersPanel;
